<template>
  <div class="glass-frosted relative rounded-8xl w-335 h-67.5 p-4">
    <div class="flex flex-wrap gap-1.5 p-2">
      <Button
        v-for="key in basicKeys"
        :key="key.keywords"
        color="normal"
        size="md"
        shape="corner"
        class="h-10"
        draggable="true"
        @click="() => handleClick(key)"
        @dragstart="(event: DragEvent) => handleDrag(key, event)"
        @drag="onDrag"
        @dragend="(event: DragEvent) => handleDragEnd(key, event)"
      >
        <Icon v-if="key.icon" :icon="key.icon" />
        {{ $t(key.labelKey) }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { Button, Icon } from '@rapoo/element'
  import type { MouseKeysCapability } from '@rapoo/device-sdk'
  import { setDragData, DragType, createMicroDragClickFallback } from '@/utils/dnd'
  import { KEYCODE_KEYWORDS_MAP, parseNumber } from '@/views/device/mouse/keys/utils/keyMapSetting'
  import { BASIC_KEYS } from '@/views/device/mouse/keys/utils/constants'
  import { useDeviceStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import { useDragImage } from '@/views/device/keyboard/keys/composables/useDragImage'
  import type { BasicKey } from '@/views/device/mouse/keys/utils/constants'
  import {
    SelectedKeySymbol,
    UpdateSelectedKeyMappingSymbol,
  } from '@/views/device/mouse/keys/types'
  import type { KeyChangePayload, KeyType, UpdateSelectedKeyMapping } from '../types'

  const { currentDevice } = storeToRefs(useDeviceStore())
  const hiddenBasicKeys = computed(() => {
    const capability = currentDevice.value?.manifest.capabilities.find(
      (item) => item.type === 'mouseKeys',
    ) as MouseKeysCapability | undefined
    return new Set(capability?.config.hiddenBasicKeys ?? [])
  })
  const basicKeys = computed(() =>
    BASIC_KEYS.filter((key) => !hiddenBasicKeys.value.has(key.keywords)),
  )

  const { setDragImage } = useDragImage()
  const { onDragStart, onDrag, shouldTreatAsClick } = createMicroDragClickFallback()

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const resolveKeyType = (type: unknown): KeyType => {
    return parseNumber(type) === 0x01 ? 'keyboard' : 'media'
  }

  const buildKeyChangePayload = (key: BasicKey): KeyChangePayload | undefined => {
    const target = KEYCODE_KEYWORDS_MAP.get(key.keywords)
    if (!target) return undefined

    return {
      keywords: selectedKey?.value?.keywords ?? '',
      keyType: resolveKeyType(target.type),
      target: target as KeyChangePayload['target'],
      raw: {
        ...target,
        icon: key?.icon,
      },
    }
  }

  const handleClick = (key: BasicKey) => {
    if (!selectedKey?.value || !updateSelectedKeyMapping) return

    const payload = buildKeyChangePayload(key)
    if (!payload) return

    updateSelectedKeyMapping(payload)
  }

  const handleDrag = (key: BasicKey, e: DragEvent) => {
    const dataTransfer = e.dataTransfer
    if (!dataTransfer) return

    const dragData = buildKeyChangePayload(key)
    if (!dragData) return

    setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)
    setDragImage(e, {
      icon: key.icon,
    })
    onDragStart(e)
  }

  /**
   * 微拖拽兜底：点击时手部微动会被浏览器误判为拖拽并吞掉 click，
   * 若本次拖拽未落到任何目标且位移极小，则按点击改键处理。
   */
  const handleDragEnd = (key: BasicKey, e: DragEvent) => {
    if (shouldTreatAsClick(e)) {
      handleClick(key)
    }
  }
</script>

<style scoped></style>
